// OGC-531 Creatives Manager v19 creative lifecycle dialogs (LIFECYCLE.md):
// archive, unarchive to Draft, delete an unused never-published draft and
// preview. Eligibility comes from the server's usage read and is rechecked by
// the server when the change runs; a refusal is shown, never a mock success.
import { useRef, useState } from 'react';
import Beacon from '../../components/Beacon.jsx';
import { notifyCuratedCatalogChanged } from '../../curated-catalog.js';
import CatalogDialog from './CatalogDialog.jsx';
import { AdPreview } from './AdPreview.jsx';
import { attemptKey, catalogErrorMessage, makeUuid } from './creatives-api.js';

// Campaigns that keep this creative, as a lower bound: campaigns whose saved
// selection uses it, or the companies with sealed campaign snapshots of it.
// Approval requests and snapshot rows repeat per campaign, so they are not
// counted directly.
export function campaignUseCount(references = {}) {
  const counted = Math.max(Number(references.campaignConfigurations) || 0, Number(references.campaignCompanies) || 0);
  return counted || (Number(references.campaignSnapshots) > 0 || Number(references.campaignApprovals) > 0 ? 1 : 0);
}

function campaignsLabel(count) {
  return `${count} campaign${count === 1 ? '' : 's'}`;
}

export default function useCreativeLifecycle({ apiClient, types = [], onChanged, onToast, onManageTypes, onUnarchived, onNotice, onDeleted }) {
  const [dialog, setDialog] = useState(null);
  const sequence = useRef(0);

  function update(patch) {
    setDialog((current) => (current ? { ...current, ...patch } : current));
  }

  function close() {
    sequence.current += 1;
    setDialog(null);
  }

  function typeFor(row) {
    return types.find((type) => type.id === row.renovation_type_id) || null;
  }

  async function loadUsage(row, token) {
    try {
      const usage = await apiClient.packageUsage(row.id);
      if (token === sequence.current) update({ usage, loading: false });
      return usage;
    } catch (error) {
      if (token === sequence.current) update({ loading: false, error: catalogErrorMessage(error, 'Campaign usage could not be checked. Try again.') });
      return null;
    }
  }

  function archive(row, returnFocus) {
    const token = ++sequence.current;
    setDialog({ kind: 'archive', row, returnFocus, key: attemptKey('archive', row.id), loading: true });
    loadUsage(row, token);
  }

  function unarchive(row, returnFocus) {
    sequence.current += 1;
    const type = typeFor(row);
    if (type?.archivedAt) {
      setDialog({ kind: 'type-archived', row, type, returnFocus });
      return;
    }
    setDialog({
      kind: 'unarchive',
      row,
      returnFocus,
      key: attemptKey('unarchive', row.id),
      ids: { versionId: makeUuid(), videoArtifactId: makeUuid(), posterArtifactId: makeUuid() },
    });
  }

  async function requestDelete(row, returnFocus) {
    const token = ++sequence.current;
    setDialog({ kind: 'delete-check', row, returnFocus, loading: true });
    const usage = await loadUsage(row, token);
    if (token !== sequence.current || !usage) return;
    setDialog({
      kind: usage.canDelete ? 'delete' : 'delete-blocked',
      row,
      returnFocus,
      usage,
      key: attemptKey('delete', row.id),
    });
  }

  async function preview(row, returnFocus) {
    const token = ++sequence.current;
    setDialog({ kind: 'preview', row, returnFocus, loading: true });
    try {
      const entry = await apiClient.entry(row.id);
      const versions = Array.isArray(entry?.versions) ? entry.versions : [];
      const version = versions.find((item) => item.id === row.version_id) || entry?.version || versions[0] || null;
      if (!version) throw Object.assign(new Error('No saved version'), { code: 'catalog_package_version_not_found' });
      const [video, poster] = await Promise.all([
        apiClient.signedUrl(version.id, 'video').catch(() => null),
        version.posterAssetId ? apiClient.signedUrl(version.id, 'poster').catch(() => null) : null,
      ]);
      if (token !== sequence.current) return;
      const mediaFailed = !/^https?:\/\//.test(video?.url || '');
      update({
        loading: false,
        error: mediaFailed ? 'The video preview could not be loaded. The copy below is the saved version.' : '',
        version,
        typeName: entry?.renovationType?.displayName || row.renovation_type,
        videoUrl: /^https?:\/\//.test(video?.url || '') ? video.url : '',
        posterUrl: /^https?:\/\//.test(poster?.url || '') ? poster.url : '',
      });
    } catch (error) {
      if (token === sequence.current) update({ loading: false, error: catalogErrorMessage(error, 'The preview could not be loaded. Try again.') });
    }
  }

  async function run(perform, success) {
    const token = sequence.current;
    update({ busy: true, error: '' });
    try {
      const result = await perform();
      if (token !== sequence.current) return;
      notifyCuratedCatalogChanged();
      setDialog(null);
      success(result);
    } catch (error) {
      if (token !== sequence.current) return;
      if (error?.code === 'catalog_renovation_type_archived' && dialog?.row) {
        setDialog({ kind: 'type-archived', row: dialog.row, type: typeFor(dialog.row), returnFocus: dialog.returnFocus });
        onChanged?.();
        return;
      }
      update({ busy: false, error: catalogErrorMessage(error) });
      if (['catalog_package_not_found', 'catalog_package_not_archived', 'catalog_package_archived'].includes(error?.code)) onChanged?.();
    }
  }

  let element = null;
  if (dialog) {
    const { kind, row, usage, busy, loading, error, returnFocus } = dialog;
    const references = usage?.references || {};
    const used = usage ? campaignUseCount(references) : 0;
    const common = { open: true, busy, error: error || '', returnFocus, onClose: close };
    const summary = (small) => (
      <div className="dialog-description-summary">{row.name}<small>{small}</small></div>
    );
    if (kind === 'archive') {
      element = (
        <CatalogDialog
          {...common}
          title="Archive this creative?"
          description="It will no longer be offered for new campaign selections."
          actions={[
            { label: 'Keep creative', autoFocus: true, onClick: close },
            { label: 'Archive creative', tone: 'danger', busyLabel: 'Archiving…', disabled: loading, onClick: () => run(
              () => apiClient.archivePackage(row.id, dialog.key),
              () => { onToast?.('Creative archived. Existing campaigns are unchanged.'); onChanged?.(); },
            ) },
          ]}
        >
          {summary(used ? `${campaignsLabel(used)} keep their saved creative.` : 'Its versions and media will be retained.')}
          <div className="notice">You can unarchive it later. It will return to Draft for review before publishing.</div>
        </CatalogDialog>
      );
    } else if (kind === 'type-archived') {
      const typeName = dialog.type?.displayName || row.renovation_type || 'Its renovation type';
      element = (
        <CatalogDialog
          {...common}
          title="Unarchive the renovation type first"
          description={`${typeName} must be active before this creative can return to Draft.`}
          actions={[
            { label: 'Keep archived', autoFocus: true, onClick: close },
            { label: 'Manage renovation types', onClick: () => { close(); onManageTypes?.(); } },
          ]}
        >
          <p>The creative will stay archived until its renovation type is available.</p>
        </CatalogDialog>
      );
    } else if (kind === 'unarchive') {
      element = (
        <CatalogDialog
          {...common}
          title="Unarchive this creative?"
          description="It will return to Draft for review before you publish it again."
          actions={[
            { label: 'Keep archived', autoFocus: true, onClick: close },
            { label: 'Unarchive to Draft', tone: 'confirm', busyLabel: 'Unarchiving…', onClick: () => run(
              () => apiClient.unarchivePackage(row.id, dialog.ids, dialog.key),
              () => {
                onToast?.('Creative unarchived as a draft. Review it before publishing.');
                onChanged?.();
                onUnarchived?.(row);
              },
            ) },
          ]}
        >
          {summary(row.pending_version_number
            ? 'Your latest saved draft changes will be kept.'
            : 'A new draft will use its saved media and copy.')}
          <div className="notice">Existing campaigns and saved versions stay unchanged. It will not be available for new campaigns until you publish.</div>
        </CatalogDialog>
      );
    } else if (kind === 'delete-check') {
      element = (
        <CatalogDialog {...common} title="Checking this creative" description="Confirming it is an unused, never-published draft."
          actions={[{ label: 'Cancel', autoFocus: true, onClick: close }]}
        >
          {loading ? <div className="dialog-loading"><Beacon size="inline" delayMs={0} slowAfterMs={null} /> Checking campaign usage…</div> : null}
        </CatalogDialog>
      );
    } else if (kind === 'delete-blocked') {
      const archived = row.status === 'archived';
      element = (
        <CatalogDialog
          {...common}
          title="This creative can’t be deleted"
          description="Only unused, never-published drafts can be permanently deleted."
          actions={[
            { label: 'Close', autoFocus: true, onClick: close },
            ...(archived ? [] : [{ label: 'Archive instead', onClick: () => archive(row, returnFocus) }]),
          ]}
        >
          {summary([
            used ? `Used by ${campaignsLabel(used)}.` : '',
            references.everPublished ? 'Published history must be retained.' : '',
            !used && !references.everPublished && row.status !== 'draft' ? `This creative is ${row.status}.` : '',
          ].filter(Boolean).join(' '))}
          <p>{archived
            ? 'This creative is already archived. Its media and history remain available to existing campaigns.'
            : 'Archive it to remove it from new campaign selections while preserving existing campaigns.'}</p>
        </CatalogDialog>
      );
    } else if (kind === 'delete') {
      element = (
        <CatalogDialog
          {...common}
          title="Delete this draft?"
          description="This permanently removes the creative and its draft history. It cannot be undone."
          actions={[
            { label: 'Keep draft', autoFocus: true, onClick: close },
            { label: 'Delete draft', tone: 'danger', busyLabel: 'Deleting…', onClick: () => run(
              () => apiClient.deletePackage(row.id, dialog.key),
              (deleted) => {
                onToast?.('Unused draft deleted.');
                if (Number(deleted?.objectCleanupPending) > 0) onNotice?.({ kind: 'cleanup', packageId: row.id });
                onChanged?.();
                onDeleted?.(row);
              },
            ) },
          ]}
        >
          {summary('Never published · Not used by any campaign')}
        </CatalogDialog>
      );
    } else if (kind === 'preview') {
      element = (
        <CatalogDialog
          {...common}
          className="preview-modal"
          title="Creative preview"
          description={`${row.name} · Version ${dialog.version?.versionNumber ?? row.version_number ?? ''}`.trim()}
          actions={[{ label: 'Close preview', autoFocus: true, onClick: close }]}
        >
          {loading
            ? <div className="dialog-loading"><Beacon size="inline" delayMs={0} slowAfterMs={null} /> Loading preview…</div>
            : dialog.version ? (
              <AdPreview
                primaryText={dialog.version.primaryText}
                headline={dialog.version.headline}
                callToAction={dialog.version.callToAction}
                videoUrl={dialog.videoUrl}
                posterUrl={dialog.posterUrl}
                mediaLabel={`${dialog.typeName || 'Renovation'} creative`}
                mediaWidth={dialog.version.videoAsset?.metadata?.width || dialog.version.posterAsset?.metadata?.width}
                mediaHeight={dialog.version.videoAsset?.metadata?.height || dialog.version.posterAsset?.metadata?.height}
              />
            ) : null}
        </CatalogDialog>
      );
    }
  }

  return {
    archive,
    unarchive,
    requestDelete,
    preview,
    close,
    dialog: element,
    open: Boolean(dialog),
  };
}
